import { z } from 'zod';

/**
 * Autoridad del wire: `docs/backend/JSON_CONTRACTS.md` v1.9.0 (abreviado
 * `JC`) y, para identidad y uplink, `docs/backend/NOTA_INTERFAZ_webxr.md`.
 *
 * Tolerancia (decisión D-5 de la tanda 1): ningún schema de respuesta del
 * servidor rechaza campos no declarados — Zod los descarta en el borde. Los
 * valores de enumeración que manda el servidor se validan como `z.string()`
 * y se estrechan con helpers `is*`; un valor nuevo no rompe el parseo.
 */

// ==============================
// Identidad del cliente (nota de interfaz §2)
// ==============================

/**
 * Valor del header HTTP `X-Client-Type`. **Constante**: este cliente es
 * `web` en todo el wire salvo el handshake WS. El login del backend solo
 * distingue `vr | pc | movil`; `vr` cambiaría el claim del JWT (audio WAV,
 * sin la sección de `expresion_detalle` en el prompt), así que este
 * cliente no puede emitirlo, ni siquiera en desarrollo.
 */
export const X_CLIENT_TYPE = 'web' as const;

/**
 * `client_type` del handshake Socket.IO (`auth.client_type`, JC §0.4).
 * Es el **único** lugar del wire donde este cliente se llama `webxr`, y es
 * el dato que lo hace destinatario `webxr` en la matriz de filtrado §3.2.
 * No es intercambiable con `web`, que resuelve a otro destinatario.
 */
export const WS_CLIENT_TYPE = 'webxr' as const;

/**
 * `tipo_interfaz` del evento `conectar` (JC §0.3). Sin efecto cuando el
 * handshake trae `client_type` (verificado en el gateway, nota §2); se
 * envía por forma del contrato.
 */
export const TIPO_INTERFAZ_CONECTAR = 'web_client' as const;

// ==============================
// Valores que decide el servidor (laxos + narrowing)
// ==============================

/**
 * Modalidad del escenario, tal como la devuelve el REST. La decide el
 * servidor (AX-12). **El REST la manda en MAYÚSCULAS** (verificado contra
 * MVP el 2026-09-19) aunque JC §0.1 y §1 dicen `text_to_speech`; el cambio
 * de casing hacia `conectar` vive en un único punto del borde
 * (`@alter/api-client`, `modalidadParaConectar`).
 */
export const MODALIDADES_CONOCIDAS = ['TEXT_TO_SPEECH', 'MULTIMODAL'] as const;
export type ModalidadConocida = (typeof MODALIDADES_CONOCIDAS)[number];
export function isModalidadConocida(value: string): value is ModalidadConocida {
  return (MODALIDADES_CONOCIDAS as readonly string[]).includes(value);
}
export const ModalidadSchema = z.string();

/**
 * Claim `tipoInterfaz` del JWT, que `GET /api/mi-simulacion` devuelve
 * (enum Prisma `TipoInterfaz`). Un cliente `web` queda como
 * `PCVoiceClient` (verificado contra MVP el 2026-09-19). Es otro
 * vocabulario que el `tipo_interfaz` de `conectar` — ver BACKLOG ítem 60
 * del core.
 */
export const TIPOS_INTERFAZ_CONOCIDOS = [
  'VRClient',
  'PCUnityClient',
  'PCVoiceClient',
  'MobileClient',
] as const;
export type TipoInterfazConocido = (typeof TIPOS_INTERFAZ_CONOCIDOS)[number];
export function isTipoInterfazConocido(value: string): value is TipoInterfazConocido {
  return (TIPOS_INTERFAZ_CONOCIDOS as readonly string[]).includes(value);
}
export const TipoInterfazSchema = z.string();

/**
 * `mime_type` del frame `audio.binario` (JC §0.2). Para este cliente el
 * servidor manda `audio/mpeg`; qué puede reproducir el cliente lo decide
 * el reproductor, no este schema.
 */
export const AUDIO_BINARIO_MIME_TYPES_CONOCIDOS = [
  'audio/mpeg',
  'audio/ogg; codecs=opus',
  'audio/wav',
] as const;
export type AudioBinarioMimeTypeConocido = (typeof AUDIO_BINARIO_MIME_TYPES_CONOCIDOS)[number];
export function isAudioBinarioMimeTypeConocido(
  value: string,
): value is AudioBinarioMimeTypeConocido {
  return (AUDIO_BINARIO_MIME_TYPES_CONOCIDOS as readonly string[]).includes(value);
}
export const AudioBinarioMimeTypeSchema = z.string();

// ==============================
// Valores que emite el cliente (estrictos)
// ==============================

/**
 * Motivos de cierre (JC §8). Catálogo cerrado en minúsculas. El servidor
 * rechaza con 400 un valor fuera de la lista.
 */
export const MOTIVOS_CIERRE = [
  'alumno',
  'timeout',
  'cuota_agotada',
  'valvula_escape',
  'error',
  'tutor',
] as const;
export type MotivoCierre = (typeof MOTIVOS_CIERRE)[number];
export const MotivoCierreSchema = z.enum(MOTIVOS_CIERRE);

/**
 * Motivo que este cliente **emite** en `POST /api/simulacion/finalizar`.
 * El resto del catálogo lo originan el servidor o el tutor.
 */
export const MotivoFinalizacionPorAlumnoSchema = z.literal('alumno');
export type MotivoFinalizacionPorAlumno = z.infer<typeof MotivoFinalizacionPorAlumnoSchema>;

/**
 * `formato` del uplink desde navegador (JC §2; nota de interfaz §3.1). Etiqueta
 * el audio hacia el STT.
 */
export const FORMATO_UPLINK = 'webm/opus' as const;

/** Respuesta `{ ok: true }` de endpoints REST mutadores. */
export const OkResponseSchema = z.object({ ok: z.literal(true) });
export type OkResponse = z.infer<typeof OkResponseSchema>;
